import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Spin, Tag, Tooltip } from "antd";
import dayjs from "dayjs";
import { fetchDocExpPayments } from "./doc-exp-api";
import { cntLineLabel, formatDate, formatDateTime, formatMoney, pick, toFlag, toNumber } from "./doc-exp-utils";

const VID_PLAT_META = {
  1: { label: "Приход", color: "green", tone: "in" },
  [-1]: { label: "Расход / Возврат", color: "red", tone: "out" },
  2: { label: "Пополнение депозита", color: "blue", tone: "dep" },
  [-2]: { label: "Возврат депозита", color: "orange", tone: "out" },
  3: { label: "Внесение в кассу", color: "cyan", tone: "in" },
  [-3]: { label: "Выплата из кассы", color: "volcano", tone: "out" },
  4: { label: "Оплата залога", color: "geekblue", tone: "dep" },
  [-4]: { label: "Возврат залога", color: "magenta", tone: "out" }
};

const normalizePaymentItem = (row) => ({
  idCnt: toNumber(pick(row, "idCnt", "IdCnt")) ?? 0,
  syma: toNumber(pick(row, "syma", "Syma")) ?? 0,
  vid: toNumber(pick(row, "vid", "Vid")) ?? 0,
  tovName: pick(row, "tovName", "TovName") ?? "",
  isAuto: toNumber(pick(row, "isAuto", "IsAuto")) ?? 0
});

const normalizePayment = (row, index) => {
  const idReg = toNumber(pick(row, "idReg", "IdReg")) ?? 0;
  const itemsRaw = pick(row, "items", "Items") || [];
  return {
    key: idReg ? String(idReg) : `pay-${index}`,
    idReg,
    vid: toNumber(pick(row, "vid", "Vid")) ?? 0,
    docnum: toNumber(pick(row, "docnum", "Docnum")) ?? 0,
    fdate: pick(row, "fdate", "Fdate") ?? null,
    syma: toNumber(pick(row, "syma", "Syma")) ?? 0,
    idKassa: toNumber(pick(row, "idKassa", "IdKassa")) ?? 0,
    kassaName: pick(row, "kassaName", "KassaName") ?? "",
    idClient: toNumber(pick(row, "idClient", "IdClient")) ?? 0,
    predName: pick(row, "predName", "PredName") ?? "",
    komment: pick(row, "komment", "Komment") ?? "",
    txtOsn: pick(row, "txtOsn", "TxtOsn") ?? "",
    avtorName: pick(row, "avtorName", "AvtorName") ?? "",
    isDel: toFlag(pick(row, "isDel", "IsDel")),
    datetimePlat: pick(row, "datetimePlat", "DatetimePlat") ?? null,
    datetimeDel: pick(row, "datetimeDel", "DatetimeDel") ?? null,
    checkVid: toNumber(pick(row, "checkVid", "CheckVid")) ?? 0,
    avansSyma: toNumber(pick(row, "avansSyma", "AvansSyma")) ?? 0,
    idCheck: toNumber(pick(row, "idCheck", "IdCheck")) ?? 0,
    items: Array.isArray(itemsRaw) ? itemsRaw.map(normalizePaymentItem) : []
  };
};

const formatSignedMoney = (value, vid) => {
  const n = Number(value) || 0;
  const abs = formatMoney(Math.abs(n));
  if (!abs) return "—";
  if (vid < 0 || n < 0) return `−${abs}`;
  return abs;
};

const MetaChip = ({ value, title }) => {
  const text = (value || "").toString().trim();
  if (!text) return null;
  return (
    <Tooltip title={title}>
      <span className="doc-exp-card__pay-chip">{text}</span>
    </Tooltip>
  );
};

const PaymentBlock = ({ item }) => {
  const vid = VID_PLAT_META[item.vid] || { label: `Вид ${item.vid}`, color: "default", tone: "neutral" };
  const isAdvance = item.checkVid === 3;
  const isService = item.checkVid === 4;
  const amount = formatSignedMoney(item.syma, item.vid);
  const weekday = item.fdate && dayjs(item.fdate).isValid() ? dayjs(item.fdate).format("dd") : "";
  const paidAt =
    item.datetimePlat && dayjs(item.datetimePlat).isValid()
      ? formatDateTime(item.datetimePlat)
      : "";
  const deletedAt =
    item.isDel && item.datetimeDel && dayjs(item.datetimeDel).isValid()
      ? formatDateTime(item.datetimeDel)
      : "";

  return (
    <article
      className={[
        "doc-exp-card__pay-item",
        `is-tone-${vid.tone || "neutral"}`,
        item.isDel ? "is-deleted" : "",
        item.vid < 0 ? "is-expense" : "is-income"
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <header className="doc-exp-card__pay-head">
        <div className="doc-exp-card__pay-date">
          <span className="doc-exp-card__pay-date-num">{formatDate(item.fdate) || "—"}</span>
          {weekday ? <span className="doc-exp-card__pay-date-wd">{weekday}</span> : null}
        </div>
        <div className="doc-exp-card__pay-head-main">
          <div className="doc-exp-card__pay-tags">
            <Tag className="doc-exp-card__pay-tag" color={item.isDel ? "default" : vid.color}>
              {vid.label}
            </Tag>
            {isAdvance ? (
              <Tag className="doc-exp-card__pay-tag" color="gold">
                Аванс
              </Tag>
            ) : null}
            {isService ? (
              <Tag className="doc-exp-card__pay-tag" color="blue">
                По услугам
              </Tag>
            ) : null}
            {item.isDel ? (
              <Tag className="doc-exp-card__pay-tag" color="default">
                Удалено
              </Tag>
            ) : null}
          </div>
          <div className="doc-exp-card__pay-sub">
            {item.docnum ? <span>№ {item.docnum}</span> : null}
            {item.kassaName ? <span>{item.kassaName}</span> : null}
          </div>
        </div>
        <div className="doc-exp-card__pay-amount" data-sign={item.vid < 0 ? "out" : "in"}>
          {amount}
        </div>
      </header>

      {(item.predName || item.txtOsn || item.komment || item.avansSyma || item.avtorName || paidAt || deletedAt) ? (
        <div className="doc-exp-card__pay-meta">
          <MetaChip title="Плательщик" value={item.predName} />
          <MetaChip title="Основание" value={item.txtOsn} />
          <MetaChip title="Комментарий" value={item.komment} />
          {item.avansSyma ? (
            <MetaChip title="Зачёт аванса" value={formatMoney(item.avansSyma)} />
          ) : null}
          <MetaChip title="Автор" value={item.avtorName} />
          <MetaChip title="Оплачено" value={paidAt} />
          <MetaChip title="Удалено" value={deletedAt} />
        </div>
      ) : null}

      {item.items?.length ? (
        <div className="doc-exp-card__pay-items">
          {item.items.map((line, i) => (
            <div key={`${item.key}-i-${i}`} className="doc-exp-card__pay-line">
              <span className="doc-exp-card__pay-line-name">{cntLineLabel(line)}</span>
              <span className="doc-exp-card__pay-line-sum">
                {formatSignedMoney(line.syma, line.vid)}
              </span>
            </div>
          ))}
        </div>
      ) : isAdvance ? (
        <div className="doc-exp-card__pay-items doc-exp-card__pay-items--empty">
          Позиций чека нет — аванс не разнесён
        </div>
      ) : null}
    </article>
  );
};

/**
 * Вкладка «Оплаты» — блоки строк REG_KASSA_MOVE (Delphi QDocPlat).
 */
const DocExpPaymentsPanel = ({ idDoc, active, reloadToken = 0 }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const reqRef = useRef(0);
  const loadedKeyRef = useRef("");

  useEffect(() => {
    if (!active) return undefined;
    if (idDoc == null) {
      setRows([]);
      setError("");
      loadedKeyRef.current = "";
      return undefined;
    }

    const key = `${idDoc}:${reloadToken}`;
    if (loadedKeyRef.current === key) return undefined;

    const requestId = ++reqRef.current;
    setLoading(true);
    setError("");
    fetchDocExpPayments(idDoc)
      .then((data) => {
        if (requestId !== reqRef.current) return;
        setRows(Array.isArray(data) ? data.map(normalizePayment) : []);
        loadedKeyRef.current = key;
      })
      .catch((e) => {
        if (requestId !== reqRef.current) return;
        setRows([]);
        loadedKeyRef.current = "";
        setError(e.message || "Не удалось загрузить оплаты");
      })
      .finally(() => {
        if (requestId === reqRef.current) setLoading(false);
      });

    return undefined;
  }, [active, idDoc, reloadToken]);

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        if (r.isDel) return acc;
        const n = Number(r.syma) || 0;
        if (r.vid < 0) acc.out += Math.abs(n);
        else acc.in += Math.abs(n);
        return acc;
      },
      { in: 0, out: 0 }
    );
  }, [rows]);

  if (idDoc == null) {
    return <Alert type="info" showIcon message="Сохраните путёвку, чтобы увидеть оплаты" />;
  }

  if (loading && rows.length === 0) {
    return (
      <div className="doc-exp-card__center">
        <Spin />
      </div>
    );
  }

  return (
    <div className="doc-exp-card__payments">
      {error ? (
        <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />
      ) : null}
      {loading ? <Spin size="small" className="doc-exp-card__payments-spin" /> : null}
      {!loading && !error && rows.length === 0 ? (
        <div className="doc-exp-card__payments-empty">Нет оплат</div>
      ) : null}
      <div className="doc-exp-card__pay-list">
        {rows.map((item) => (
          <PaymentBlock key={item.key} item={item} />
        ))}
      </div>
      {rows.length && (totals.in > 0 || totals.out > 0) ? (
        <footer className="doc-exp-card__pay-footer">
          <span className="doc-exp-card__pay-footer-label">Итого</span>
          <div className="doc-exp-card__pay-footer-parts">
            {totals.in > 0 ? <span className="is-in">приход {formatMoney(totals.in)}</span> : null}
            {totals.out > 0 ? <span className="is-out">расход {formatMoney(totals.out)}</span> : null}
          </div>
          <span className="doc-exp-card__pay-footer-total">
            {formatSignedMoney(totals.in - totals.out, totals.in - totals.out < 0 ? -1 : 1)}
          </span>
        </footer>
      ) : null}
    </div>
  );
};

export default DocExpPaymentsPanel;
